'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { marketplaceApi } from '@/lib/api';

export function ProductImageStudio({ productId, businessId, disabled, onSelect }: {
  productId: string; businessId?: string; disabled: boolean; onSelect: (file: File) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [draft, setDraft] = useState<{ imageDataUrl: string; referenceUsed: boolean } | null>(null);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => { request.current?.abort(); }, []);

  async function generate() {
    if (request.current || disabled) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError('');
    setDraft(null);
    try {
      const result = await marketplaceApi.previewImage(productId, prompt, businessId, controller.signal);
      if (!controller.signal.aborted) setDraft(result);
    } catch (err) {
      if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Could not generate an image. Please try again.');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
      request.current = null;
    }
  }

  function select() {
    if (!draft) return;
    const bytes = Uint8Array.from(atob(draft.imageDataUrl.split(',')[1]), char => char.charCodeAt(0));
    onSelect(new File([bytes], 'generated-product.webp', { type: 'image/webp' }));
    setDraft(null);
  }

  return <div className="mt-3 space-y-3 rounded-lg border bg-muted/30 p-3">
    <div className="flex items-center justify-between gap-3">
      <div><p className="text-sm font-medium">AI image studio</p><p className="text-xs text-muted-foreground">Studio lighting · transparent background</p></div>
      <Button type="button" variant="outline" size="sm" disabled={busy || disabled} onClick={generate}>
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
        {busy ? 'Generating…' : 'Generate image'}
      </Button>
    </div>
    <textarea aria-label="Image styling instructions" value={prompt} onChange={e => setPrompt(e.target.value)} maxLength={500}
      disabled={busy || disabled} rows={2} placeholder="Optional: show the filling, use a front view…"
      className="w-full resize-none rounded-md border bg-background p-2 text-sm" />
    <p className="text-xs text-muted-foreground">Uses saved menu details and an uploaded photo when available. Each generation uses one AI edit. Select a preview, then Save to publish.</p>
    {busy && <p role="status" className="text-xs text-muted-foreground">Creating your preview. This can take up to 3 minutes.</p>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {draft && <div className="space-y-2">
      <div className="relative aspect-square max-h-72 rounded-lg bg-[#e9e6dd]">
        <Image src={draft.imageDataUrl} alt="Generated image preview" fill unoptimized className="object-contain p-3" />
      </div>
      <p className="text-xs text-muted-foreground">{draft.referenceUsed ? 'Based on your saved photo.' : 'Concept based on your menu description.'} Check ingredients and appearance before using.</p>
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={disabled} onClick={select}>Use image</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
      </div>
    </div>}
  </div>;
}
